/**
 * chart.tsx — minimal recharts wrapper for shadcn-compatible chart primitives.
 *
 * Provides:
 *   - ChartContainer: wraps ResponsiveContainer with CSS-variable theming
 *   - ChartTooltip / ChartTooltipContent: styled tooltip for recharts
 *   - RadialScoreChart: small inline radial bar (used in page detail sidebar)
 *   - ScoreShapeCard: shadcn "Radial Chart - Shape" style card for document-level scores
 *   - GradeChip: coloured pill showing a quality grade string
 */

import type * as React from 'react'
import {
  Label,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/utils/cn'

// ─── CSS variable colour map ─────────────────────────────────────────────────

export type ChartColorKey = 'chart-1' | 'chart-2' | 'chart-3' | 'chart-4' | 'chart-5'

// ─── ChartContainer ──────────────────────────────────────────────────────────

interface ChartContainerProps {
  children: React.ReactNode
  className?: string
  height?: number | string
}

export function ChartContainer({ children, className, height = 200 }: ChartContainerProps) {
  return (
    <div className={cn('w-full', className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children as React.ReactElement}
      </ResponsiveContainer>
    </div>
  )
}

// ─── ChartTooltipContent ─────────────────────────────────────────────────────

interface TooltipPayloadEntry {
  name?: string
  value?: number | string
  color?: string
}

interface ChartTooltipContentProps {
  active?: boolean
  payload?: TooltipPayloadEntry[]
  label?: string
  formatter?: (value: number | string, name: string) => React.ReactNode
}

export function ChartTooltipContent({
  active,
  payload,
  label,
  formatter,
}: ChartTooltipContentProps) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      {label && <p className="mb-1 font-medium text-popover-foreground">{label}</p>}
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-muted-foreground">
          {entry.color && (
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: entry.color }}
            />
          )}
          <span>{entry.name}:</span>
          <span className="font-medium text-popover-foreground">
            {formatter ? formatter(entry.value ?? 0, entry.name ?? '') : entry.value}
          </span>
        </div>
      ))}
    </div>
  )
}

export const ChartTooltip = Tooltip

// ─── RadialScoreChart ────────────────────────────────────────────────────────
// Small inline radial bar (80×80) used in PageDetailSidebar and legacy spots.

interface RadialScoreChartProps {
  score: number | null | undefined
  label: string
  colorVar?: ChartColorKey
  className?: string
}

export function RadialScoreChart({
  score,
  label,
  colorVar = 'chart-1',
  className,
}: RadialScoreChartProps) {
  const pct = score != null ? Math.round(score * 100) : null
  const fill = `var(--${colorVar})`

  const data = [{ name: label, value: pct ?? 0, fill }]

  return (
    <div className={cn('flex flex-col items-center gap-1', className)}>
      <div className="relative" style={{ width: 80, height: 80 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="65%"
            outerRadius="90%"
            startAngle={90}
            endAngle={-270}
            data={data}
            barSize={8}
          >
            <RadialBar
              dataKey="value"
              cornerRadius={4}
              background={{ fill: 'var(--muted)' }}
              max={100}
            />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          {pct != null ? (
            <span className="text-sm font-semibold text-foreground">{pct}%</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>
      </div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}

// ─── ScoreShapeCard ──────────────────────────────────────────────────────────
// shadcn "Radial Chart - Shape" style card for document-level confidence scores.
// The arc sweeps clockwise from the top proportionally to the score (0–1).

export interface ScoreShapeCardProps {
  score: number | null | undefined
  label: string
  colorVar?: ChartColorKey
}

export function ScoreShapeCard({ score, label, colorVar = 'chart-1' }: ScoreShapeCardProps) {
  const pct = score != null ? Math.round(score * 100) : null
  // Arc from top (90°) sweeps clockwise; full circle = score 1.0
  const endAngle = 90 - (pct ?? 0) * 3.6
  const chartData = [{ value: pct ?? 0, fill: `var(--${colorVar})` }]

  return (
    <Card className="flex flex-col border-border bg-card">
      <CardHeader className="items-center pb-0">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 px-2 pt-0 pb-4">
        {/* overflow-visible lets the arc render outside the SVG viewport without clipping */}
        <div className="mx-auto aspect-square w-full max-h-[160px] [&_svg]:overflow-visible">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              data={chartData}
              startAngle={90}
              endAngle={endAngle}
              innerRadius="52%"
              outerRadius="74%"
              margin={{ top: 12, right: 12, bottom: 12, left: 12 }}
            >
              {/* Two concentric filled circles: outer=muted (grey track), inner=background (hole) */}
              <PolarGrid
                gridType="circle"
                radialLines={false}
                stroke="none"
                className="first:fill-muted last:fill-background"
                polarRadius={[58, 48]}
              />
              <RadialBar dataKey="value" background cornerRadius={5} />
              <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                      return (
                        <text
                          x={viewBox.cx}
                          y={viewBox.cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="fill-foreground text-lg font-bold"
                          >
                            {pct != null ? `${pct}%` : '—'}
                          </tspan>
                        </text>
                      )
                    }
                  }}
                />
              </PolarRadiusAxis>
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── GradeChip ───────────────────────────────────────────────────────────────

const GRADE_CLASSES: Record<string, string> = {
  excellent: 'bg-chart-2/20 text-chart-2 border-chart-2/30',
  good: 'bg-chart-4/20 text-chart-4 border-chart-4/30',
  fair: 'bg-chart-5/20 text-chart-5 border-chart-5/30',
  poor: 'bg-destructive/20 text-destructive border-destructive/30',
}

interface GradeChipProps {
  grade: string | null | undefined
  className?: string
}

export function GradeChip({ grade, className }: GradeChipProps) {
  if (!grade) return <span className="text-muted-foreground text-xs">—</span>
  const classes =
    GRADE_CLASSES[grade.toLowerCase()] ?? 'bg-muted text-muted-foreground border-border'
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium capitalize',
        classes,
        className
      )}
    >
      {grade}
    </span>
  )
}
